import { Record, Map, List } from 'immutable';
import moment from 'moment';
import { LOCATION_CHANGE } from 'connected-react-router';
import { matchPath } from 'react-router';
import { Utils } from '@kineticdata/bundle-common';
import { CategoryHelper } from '@kineticdata/bundle-services-custom';
import { parse } from 'query-string';
const { noPayload, withPayload } = Utils;
const ns = Utils.namespaceBuilder('app');

const AUTH_ROUTES = ['/login', '/reset-password', '/create-account'];

export const types = {
  FETCH_APP_REQUEST: ns('FETCH_APP_REQUEST'),
  FETCH_APP_SUCCESS: ns('FETCH_APP_SUCCESS'),
  FETCH_APP_FAILURE: ns('FETCH_APP_FAILURE'),
  FETCH_APP_RESET: ns('FETCH_APP_RESET'),

  FETCH_SPACE_REQUEST: ns('FETCH_SPACE_REQUEST'),
  FETCH_SPACE_SUCCESS: ns('FETCH_SPACE_SUCCESS'),
  FETCH_SPACE_FAILURE: ns('FETCH_SPACE_FAILURE'),

  FETCH_KAPPS_REQUEST: ns('FETCH_KAPPS_REQUEST'),
  FETCH_KAPPS_SUCCESS: ns('FETCH_KAPPS_SUCCESS'),
  FETCH_KAPPS_FAILURE: ns('FETCH_KAPPS_FAILURE'),

  FETCH_PORTAL_PAGES_COMPLETE: ns('FETCH_PORTAL_PAGES_COMPLETE'),
  FETCH_FORMS_COMPLETE: ns('FETCH_FORMS_COMPLETE'),
  FETCH_CATEGORIES_COMPLETE: ns('FETCH_CATEGORIES_COMPLETE'),

  FETCH_PROFILE_REQUEST: ns('FETCH_PROFILE_REQUEST'),
  FETCH_PROFILE_SUCCESS: ns('FETCH_PROFILE_SUCCESS'),
  FETCH_PROFILE_FAILURE: ns('FETCH_PROFILE_FAILURE'),
  UPDATE_PROFILE_REQUEST: ns('UPDATE_PROFILE_REQUEST'),
  UPDATE_PROFILE_COMPLETE: ns('UPDATE_PROFILE_COMPLETE'),
  TOGGLE_FAVORITE_REQUEST: ns('TOGGLE_FAVORITE_REQUEST'),
  UPDATE_FAVORITES_CACHE: ns('UPDATE_FAVORITES_CACHE'),

  FETCH_LOCALE_META_REQUEST: ns('FETCH_LOCALE_META_REQUEST'),
  FETCH_LOCALE_META_SUCCESS: ns('FETCH_LOCALE_META_SUCCESS'),
  FETCH_LOCALE_META_FAILURE: ns('FETCH_LOCALE_META_FAILURE'),

  FETCH_SUBTEAMS_REQUEST: ns('FETCH_SUBTEAMS_REQUEST'),
  FETCH_SUBTEAMS_COMPLETE: ns('FETCH_SUBTEAMS_COMPLETE'),
  FETCH_USERS_REQUEST: ns('FETCH_USERS_REQUEST'),
  FETCH_USERS_COMPLETE: ns('FETCH_USERS_COMPLETE'),

  SET_USER_LOCALE: ns('SET_USER_LOCALE'),
  SET_KAPP_SLUG: ns('SET_KAPP_SLUG'),
  SET_CORE_VERSION: ns('SET_CORE_VERSION'),

  SET_AUTHENTICATED: ns('SET_AUTHENTICATED'),
  CLEAR_AUTHENTICATED_TIMESTAMP: ns('CLEAR_AUTHENTICATED_TIMESTAMP'),
};

export const actions = {
  fetchApp: withPayload(types.FETCH_APP_REQUEST),
  fetchAppSuccess: withPayload(types.FETCH_APP_SUCCESS),
  fetchAppFailure: withPayload(types.FETCH_APP_FAILURE),
  fetchAppReset: withPayload(types.FETCH_APP_RESET),

  fetchSpaceRequest: noPayload(types.FETCH_SPACE_REQUEST),
  fetchSpaceSuccess: withPayload(types.FETCH_SPACE_SUCCESS),
  fetchSpaceFailure: withPayload(types.FETCH_SPACE_FAILURE),

  fetchKappsRequest: noPayload(types.FETCH_KAPPS_REQUEST),
  fetchKappsSuccess: withPayload(types.FETCH_KAPPS_SUCCESS),
  fetchKappsFailure: withPayload(types.FETCH_KAPPS_FAILURE),

  fetchPortalPagesComplete: withPayload(types.FETCH_PORTAL_PAGES_COMPLETE),
  fetchFormsComplete: withPayload(types.FETCH_FORMS_COMPLETE),
  fetchCategoriesComplete: withPayload(types.FETCH_CATEGORIES_COMPLETE),

  fetchProfileRequest: noPayload(types.FETCH_PROFILE_REQUEST),
  fetchProfileSuccess: withPayload(types.FETCH_PROFILE_SUCCESS),
  fetchProfileFailure: withPayload(types.FETCH_PROFILE_FAILURE),
  updateProfileRequest: withPayload(types.UPDATE_PROFILE_REQUEST),
  updateProfileComplete: withPayload(types.UPDATE_PROFILE_COMPLETE),
  toggleFavoriteRequest: withPayload(types.TOGGLE_FAVORITE_REQUEST),
  updateFavoritesCache: withPayload(types.UPDATE_FAVORITES_CACHE),

  fetchLocaleMetaRequest: noPayload(types.FETCH_LOCALE_META_REQUEST),
  fetchLocaleMetaSuccess: withPayload(types.FETCH_LOCALE_META_SUCCESS),
  fetchLocaleMetaFailure: withPayload(types.FETCH_LOCALE_META_FAILURE),

  fetchSubteamsRequest: withPayload(types.FETCH_SUBTEAMS_REQUEST),
  fetchSubteamsComplete: withPayload(types.FETCH_SUBTEAMS_COMPLETE),
  fetchUsersRequest: withPayload(types.FETCH_USERS_REQUEST),
  fetchUsersComplete: withPayload(types.FETCH_USERS_COMPLETE),

  setUserLocale: withPayload(types.SET_USER_LOCALE),
  setKappSlug: withPayload(types.SET_KAPP_SLUG),
  setCoreVersion: withPayload(types.SET_CORE_VERSION),

  setAuthenticated: withPayload(types.SET_AUTHENTICATED),
  clearAuthenticatedTimestamp: noPayload(types.CLEAR_AUTHENTICATED_TIMESTAMP),
};

export const State = Record({
  coreVersion: null,
  locales: null,
  timezones: null,
  locale: moment.locale(),
  space: null,
  kapp: null,
  kapps: List(),
  portalPages: List(),
  profile: null,
  profileUpdating: false,
  favoritesCache: null,
  favoritesCacheTimestamp: null,
  errors: List(),
  kappSlug: null,
  loading: true,
  usersMap: Map(),
  subteamsMap: Map(),
  authenticated: null,
  authenticatedTimestamp: null,
  authRedirect: null,
  authRoute: '/login',
});

export const reducer = (state = State(), { type, payload }) => {
  switch (type) {
    case types.FETCH_APP_REQUEST:
      return state.set('errors', List());
    case types.FETCH_APP_SUCCESS:
      return state.set('errors', List()).set('loading', false);
    case types.FETCH_APP_FAILURE:
      return state
        .update(
          'errors',
          errors =>
            errors.isEmpty()
              ? errors.push({ message: 'Unexpected error occurred' })
              : errors,
        )
        .set('loading', false);
    case types.FETCH_APP_RESET:
      return state.set('loading', true);

    case types.FETCH_SPACE_SUCCESS:
      return state.set('space', payload);
    case types.FETCH_SPACE_FAILURE:
      return state.update('errors', errors => errors.push(payload));

    case types.FETCH_KAPPS_SUCCESS:
      return state
        .set('kapps', List(payload))
        .set('kapp', payload.find(kapp => kapp.slug === state.kappSlug));
    case types.FETCH_KAPPS_FAILURE:
      return state.update('errors', errors => errors.push(payload));

    case types.FETCH_PORTAL_PAGES_COMPLETE:
      return state.set('portalPages', List(payload));
    case types.FETCH_FORMS_COMPLETE:
      return state
        .update('kapps', kapps =>
          kapps.map(kapp => {
            const kappForms = payload.find(p => p.slug === kapp.slug);
            if (kappForms && kappForms.forms) {
              return { ...kapp, forms: kappForms.forms };
            } else {
              return kapp;
            }
          }),
        )
        .update(s =>
          s.set('kapp', s.kapps.find(kapp => kapp.slug === s.kappSlug)),
        );
    case types.FETCH_CATEGORIES_COMPLETE:
      return state
        .update('kapps', kapps =>
          kapps.map(kapp => {
            const kappCategories = payload.find(p => p.slug === kapp.slug);
            if (kappCategories && kappCategories.categories) {
              return {
                ...kapp,
                categories: kappCategories.categories,
                categoryHelper: CategoryHelper(kappCategories.categories),
              };
            } else {
              return kapp;
            }
          }),
        )
        .update(s =>
          s.set('kapp', s.kapps.find(kapp => kapp.slug === s.kappSlug)),
        );

    case types.FETCH_PROFILE_SUCCESS:
      return state.set('profile', payload);
    case types.FETCH_PROFILE_FAILURE:
      return state.update('errors', errors => errors.push(payload));
    case types.UPDATE_PROFILE_REQUEST:
      return state.set('profileUpdating', true);
    case types.UPDATE_PROFILE_COMPLETE:
      return state
        .set('profile', payload || state.profile)
        .set('profileUpdating', false);
    case types.UPDATE_FAVORITES_CACHE:
      return state
        .set('favoritesCache', payload || null)
        .set('favoritesCacheTimestamp', new Date().getTime());

    case types.FETCH_LOCALE_META_SUCCESS:
      return state
        .set('timezones', payload.timezones)
        .set('locales', payload.locales);
    case types.FETCH_LOCALE_META_FAILURE:
      return state.update('errors', errors => errors.push(payload));

    case types.FETCH_SUBTEAMS_COMPLETE:
      return state
        .setIn(['subteamsMap', payload.teamName], List(payload.subteams))
        .update('usersMap', usersMap =>
          // Update the users map with all users in the retrieved teams
          payload.subteams
            .flatMap(({ memberships }) => memberships.map(m => m.user))
            .reduce((map, user) => map.set(user.username, user), usersMap),
        );
    case types.FETCH_USERS_REQUEST:
      return state.update('usersMap', usersMap =>
        payload.usernames.reduce(
          (map, username) =>
            !map.has(username) ? map.set(username, null) : map,
          usersMap,
        ),
      );
    case types.FETCH_USERS_COMPLETE:
      return state.update('usersMap', usersMap =>
        // Update map for any users that weren't found to false
        payload.usernames.reduce(
          (map, username) =>
            !map.has(username) ? map.set(username, false) : map,
          // Set all fetched users into map
          payload.users.reduce(
            (map, user) => map.set(user.username, user),
            usersMap,
          ),
        ),
      );

    case types.SET_USER_LOCALE:
      return state.set('locale', payload || moment.locale());
    case types.SET_KAPP_SLUG:
      return state
        .set('kappSlug', payload)
        .set('kapp', state.kapps.find(kapp => kapp.slug === payload));
    case types.SET_CORE_VERSION:
      return state.set('coreVersion', payload);
    case LOCATION_CHANGE:
      const path = '/kapps/:kappSlug';
      const match = matchPath(payload.location.pathname, { path });
      const kappSlug = match && match.params.kappSlug;
      return state
        .set('kappSlug', kappSlug)
        .set('kapp', state.kapps.find(kapp => kapp.slug === kappSlug))
        .set(
          'authRedirect',
          !state.authenticated
            ? !AUTH_ROUTES.some(r => payload.location.pathname.startsWith(r))
              ? `${payload.location.pathname}${payload.location.search}`
              : state.authRedirect
            : null,
        )
        .set(
          'authRoute',
          payload.location.search
            ? parse(payload.location.search).invitationToken
              ? `/create-account${payload.location.search}`
              : `/login${payload.location.search}`
            : '/login',
        )
        .update(
          'loading',
          value =>
            !state.authenticated &&
            AUTH_ROUTES.some(r => payload.location.pathname.startsWith(r))
              ? true
              : value,
        );
    case types.SET_AUTHENTICATED:
      return state
        .set('loading', payload !== state.authenticated)
        .set('authenticated', payload)
        .update(
          'authenticatedTimestamp',
          ts => (!!payload && !state.authenticated ? Date.now() : ts),
        );
    case types.CLEAR_AUTHENTICATED_TIMESTAMP:
      return state.set('authenticatedTimestamp', null);
    default:
      return state;
  }
};
